/**
 * RegionalSalesPacing — "Event Pacing Monitor" card: on-pace / soft /
 * at-risk summary tiles plus per-workshop pace bars with an
 * expected-pace marker (linear curve: 0% at 42 days out → 100% on
 * event day; BON-82 review decision).
 *
 * Live when the DTO ships `pacing`; otherwise the design's structure
 * renders as an em-dash skeleton behind <MockedOverlay> (BON-138).
 */
import { MockedOverlay } from '../primitives';

import type { RegionalSalesDto, SalesPaceRow } from './regionalApi';

const EM_DASH = '—';

const STATUS_COLOR: Record<SalesPaceRow['status'], string> = {
  'on-pace': 'var(--nb-primary)',
  soft: 'var(--nb-warn)',
  risk: 'var(--nb-risk)',
};

/** Layout-only skeleton fills for the mocked state. */
const SKELETON_ROWS = [62, 48, 71, 35, 80] as const;

function fmtDate(dateIso: string): string {
  return new Date(`${dateIso}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function SummaryTile({ label, color, n }: { label: string; color: string; n: string }) {
  return (
    <div
      style={{
        flex: 1,
        padding: '10px 12px',
        background: 'var(--nb-surface)',
        borderRadius: 8,
        borderLeft: `3px solid ${color}`,
      }}
    >
      <div className="nb-tabular" style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>
        {n}
      </div>
      <div style={{ fontSize: 11, color: 'var(--nb-muted)', marginTop: 4 }}>{label}</div>
    </div>
  );
}

function SummaryTiles({ pacing }: { pacing: RegionalSalesDto['pacing'] }) {
  return (
    <div style={{ display: 'flex', gap: 14, marginBottom: 12 }}>
      <SummaryTile
        label="On pace"
        color={STATUS_COLOR['on-pace']}
        n={pacing ? String(pacing.onPace) : EM_DASH}
      />
      <SummaryTile
        label="Soft (needs push)"
        color={STATUS_COLOR.soft}
        n={pacing ? String(pacing.soft) : EM_DASH}
      />
      <SummaryTile
        label="At risk"
        color={STATUS_COLOR.risk}
        n={pacing ? String(pacing.atRisk) : EM_DASH}
      />
    </div>
  );
}

function PaceBar({
  fillPct,
  expectedPct,
  color,
}: {
  fillPct: number;
  expectedPct: number;
  color: string;
}) {
  return (
    <div
      style={{
        position: 'relative',
        width: 140,
        height: 8,
        background: 'var(--nb-surface-2)',
        borderRadius: 4,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: `${fillPct}%`,
          background: color,
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: `${expectedPct}%`,
          top: -2,
          bottom: -2,
          width: 2,
          background: 'var(--nb-ink-2)',
          opacity: 0.55,
        }}
      />
    </div>
  );
}

function LiveRow({ row }: { row: SalesPaceRow }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ width: 64, fontSize: 11, fontWeight: 600 }}>{fmtDate(row.dateIso)}</span>
      <span style={{ width: 36, fontSize: 11, color: 'var(--nb-muted)' }}>{row.venueShort}</span>
      <span
        style={{
          flex: 1,
          fontSize: 11.5,
          color: 'var(--nb-muted)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {row.label}
      </span>
      <PaceBar
        fillPct={row.fillPct}
        expectedPct={row.expectedPct}
        color={STATUS_COLOR[row.status]}
      />
      <span
        className="nb-tabular nb-mono"
        style={{ width: 48, textAlign: 'right', fontSize: 10.5, color: 'var(--nb-subtle)' }}
      >
        {row.sold}/{row.cap}
      </span>
    </div>
  );
}

function SkeletonRow({ fillPct }: { fillPct: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ width: 64, fontSize: 11, fontWeight: 600, color: 'var(--nb-subtle)' }}>
        {EM_DASH}
      </span>
      <span style={{ width: 36, fontSize: 11, color: 'var(--nb-subtle)' }}>{EM_DASH}</span>
      <span style={{ flex: 1, fontSize: 11.5, color: 'var(--nb-subtle)' }}>{EM_DASH}</span>
      <PaceBar fillPct={fillPct} expectedPct={70} color="var(--nb-border-2)" />
      <span
        className="nb-tabular nb-mono"
        style={{ width: 48, textAlign: 'right', fontSize: 10.5, color: 'var(--nb-subtle)' }}
      >
        {EM_DASH}
      </span>
    </div>
  );
}

export function RegionalSalesPacing({ pacing }: { pacing: RegionalSalesDto['pacing'] }) {
  return (
    <section className="nb-card">
      <div className="nb-card-hd">
        <h3 className="nb-display" style={{ fontSize: 13, letterSpacing: '0.04em', margin: 0 }}>
          Event Pacing Monitor
        </h3>
        <span className="nb-eyebrow">are workshops on pace to sell out?</span>
      </div>
      {pacing ? (
        <>
          <SummaryTiles pacing={pacing} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {pacing.rows.map((row) => (
              <LiveRow key={`${row.dateIso}-${row.venueShort}-${row.label}`} row={row} />
            ))}
            {pacing.rows.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--nb-subtle)' }}>
                No workshops scheduled in the next 6 weeks.
              </div>
            )}
          </div>
        </>
      ) : (
        <MockedOverlay reason="Per-event pacing source is unavailable. Tracked in BON-138.">
          <SummaryTiles pacing={null} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {SKELETON_ROWS.map((pct) => (
              <SkeletonRow key={pct} fillPct={pct} />
            ))}
          </div>
        </MockedOverlay>
      )}
    </section>
  );
}
